import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { CiSearch } from "react-icons/ci";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import {
  useCancelQuickPaymentLinkMutation,
  useGetQuickPaymentLinksQuery,
  useSendQuickPaymentLinkMutation,
} from "../../Api/attoDeskApi";
import { useNotifier } from "../../Core/Notifier";
import useDebounce from "../../Components/SearchDebonce/useDebounce";
import Pagination from "../../Components/Pagination";
import { formatCurrency } from "../../Core/utils/money";

const BRAND = "#2f3891";
const ROWS_PER_PAGE = 25;

type LinkRow = {
  id: number;
  token: string;
  url: string | null;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string | null;
  reference_number: string;
  amount: number;
  currency: string;
  status: string;
  status_label: string;
  bambora_transaction_id: string | null;
  paid_at: string | null;
  expires_at: string | null;
  created_at: string | null;
};

/**
 * Status colours. Paid is green, cancelled/expired are deliberately grey rather
 * than red — they are administrative endings, not failures, and colouring them
 * like a decline sends staff chasing a bank.
 */
const STATUS_STYLE: Record<string, { bg: string; color: string; border: string }> = {
  pending:   { bg: "#fffbeb", color: "#b45309", border: "#fde68a" },
  paid:      { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" },
  declined:  { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" },
  cancelled: { bg: "#f8fafc", color: "#64748b", border: "#e2e8f0" },
  expired:   { bg: "#f8fafc", color: "#64748b", border: "#e2e8f0" },
};

const STATUSES = ["", "pending", "paid", "declined", "cancelled", "expired"];

const formatDate = (value: string | null) =>
  value ? dayjs(value).format("MMM D, YYYY h:mm A") : "—";

const thSx = {
  fontWeight: 600,
  color: "#64748b",
  fontSize: 12,
  bgcolor: "#f8fafc",
  borderBottom: "1px solid #e2e8f0",
  whiteSpace: "nowrap" as const,
};
const tdSx = { fontSize: 13, borderBottom: "1px solid #f1f5f9" };

/**
 * Payment requests and their lifecycle.
 *
 * Actions are status-aware: a paid request must not offer a way to pay it
 * again, and a cancelled or expired one must not offer one at all. What each
 * status allows is decided in one place (`actionsFor`) so the table and any
 * future screen cannot disagree about it.
 */
const actionsFor = (status: string) => ({
  // Copy and open stay available wherever the link still leads somewhere
  // meaningful — including paid, where it shows the completed notice.
  canCopy:   status !== "cancelled" && status !== "expired",
  canOpen:   status !== "cancelled" && status !== "expired",
  // Resending is for requests still awaiting payment. A declined one is
  // included: the card failed, the request did not.
  canResend: status === "pending" || status === "declined",
  canCancel: status === "pending" || status === "declined",
});

const PaymentLinksTab: React.FC = () => {
  const { showMessage, showErrorMessage } = useNotifier();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const debouncedSearch = useDebounce(search, 350);

  const { data, isFetching, isError, refetch } = useGetQuickPaymentLinksQuery({
    search: debouncedSearch,
    status,
    page: page + 1,
    per_page: ROWS_PER_PAGE,
  });

  const [sendLink] = useSendQuickPaymentLinkMutation();
  const [cancelLink] = useCancelQuickPaymentLinkMutation();

  const rows: LinkRow[] = useMemo(() => (data?.data as LinkRow[]) ?? [], [data]);
  const meta: any = (data as any)?.meta ?? {};
  const total = meta.total ?? 0;

  const changeFilter = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(0);
  };

  const handleCopy = async (row: LinkRow) => {
    if (!row.url) return;
    try {
      await navigator.clipboard.writeText(row.url);
      setCopiedId(row.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      showErrorMessage("Could not copy automatically — open the link and copy it from the address bar");
    }
  };

  const handleResend = async (row: LinkRow) => {
    setBusyId(row.id);
    try {
      const res: any = await sendLink({ id: row.id, customer: true, admin: false }).unwrap();
      showMessage(res?.message ?? "Payment request sent", "success");
    } catch (err: any) {
      showErrorMessage(err?.data?.message ?? "The email could not be sent");
    } finally {
      setBusyId(null);
    }
  };

  const handleCancel = async (row: LinkRow) => {
    setBusyId(row.id);
    try {
      const res: any = await cancelLink({ id: row.id }).unwrap();
      showMessage(res?.message ?? "Payment link cancelled", "success");
      refetch();
    } catch (err: any) {
      showErrorMessage(err?.data?.message ?? "The payment link could not be cancelled");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box>
      {/* ── Filters ──────────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", gap: 1.5, p: 2, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          size="small"
          placeholder="Customer, email, phone, reference, token"
          value={search}
          onChange={(e) => changeFilter(setSearch)(e.target.value)}
          sx={{ minWidth: 300, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CiSearch size={18} color="#94a3b8" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          size="small"
          select
          label="Status"
          value={status}
          onChange={(e) => changeFilter(setStatus)(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          {STATUSES.map((s) => (
            <MenuItem key={s || "all"} value={s}>
              {s === "" ? "All statuses" : s.charAt(0).toUpperCase() + s.slice(1)}
            </MenuItem>
          ))}
        </TextField>
        {(search || status) && (
          <Button
            size="small"
            onClick={() => {
              setSearch("");
              setStatus("");
              setPage(0);
            }}
            sx={{ textTransform: "none", color: "#64748b" }}
          >
            Clear
          </Button>
        )}
      </Box>

      {meta.payment_url_configured === false && (
        <Box sx={{ mx: 2, mb: 2, p: 1.5, borderRadius: 2, bgcolor: "#fef2f2", border: "1px solid #fecaca" }}>
          <Typography sx={{ fontSize: 12, color: "#b91c1c" }}>
            Payment URL is not configured. Set it under Settings → URL — until then these links have no
            address and cannot be copied or sent.
          </Typography>
        </Box>
      )}

      {/* ── Table ────────────────────────────────────────────────────────── */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={thSx}>Created</TableCell>
              <TableCell sx={thSx}>Customer</TableCell>
              <TableCell sx={thSx}>Reference</TableCell>
              <TableCell sx={{ ...thSx, textAlign: "right" }}>Amount</TableCell>
              <TableCell sx={thSx}>Status</TableCell>
              <TableCell sx={thSx}>Paid</TableCell>
              <TableCell sx={{ ...thSx, textAlign: "right" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: "center", py: 6, border: 0 }}>
                  <CircularProgress size={22} sx={{ color: BRAND }} />
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: "center", py: 6, border: 0, color: "#dc2626" }}>
                  Could not load payment links.
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: "center", py: 6, border: 0, color: "#94a3b8" }}>
                  No payment links yet. Use “Create Payment Link” to send one.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const can = actionsFor(row.status);
                const style = STATUS_STYLE[row.status] ?? STATUS_STYLE.cancelled;
                const busy = busyId === row.id;

                return (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ ...tdSx, color: "#475569", whiteSpace: "nowrap" }}>
                      {formatDate(row.created_at)}
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
                        {row.customer_name}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                        {row.customer_email || row.customer_phone || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...tdSx, color: "#475569" }}>{row.reference_number}</TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: "right", fontWeight: 700 }}>
                      {formatCurrency(row.amount)} {row.currency}
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Chip
                        label={row.status_label}
                        size="small"
                        sx={{
                          bgcolor: style.bg,
                          color: style.color,
                          border: `1px solid ${style.border}`,
                          fontWeight: 700,
                          fontSize: 10,
                          height: 20,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...tdSx, color: "#475569", whiteSpace: "nowrap" }}>
                      {row.paid_at ? formatDate(row.paid_at) : "—"}
                    </TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: "right", whiteSpace: "nowrap" }}>
                      {busy ? (
                        <CircularProgress size={16} sx={{ color: BRAND }} />
                      ) : (
                        <>
                          {can.canCopy && row.url && (
                            <Tooltip title={copiedId === row.id ? "Copied" : "Copy link"} arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleCopy(row)}
                                sx={{ color: copiedId === row.id ? "#16a34a" : BRAND }}
                              >
                                {copiedId === row.id ? (
                                  <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                                ) : (
                                  <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                          {can.canOpen && row.url && (
                            <Tooltip title="Open link" arrow>
                              <IconButton
                                size="small"
                                component="a"
                                href={row.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ color: "#64748b" }}
                              >
                                <OpenInNewOutlinedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {can.canResend && (
                            <Tooltip
                              title={row.customer_email ? "Resend to customer" : "No customer email on this request"}
                              arrow
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!row.customer_email}
                                  onClick={() => handleResend(row)}
                                  sx={{ color: "#64748b" }}
                                >
                                  <ForwardToInboxOutlinedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {can.canCancel && (
                            <Tooltip title="Cancel request" arrow>
                              <IconButton size="small" onClick={() => handleCancel(row)} sx={{ color: "#dc2626" }}>
                                <BlockOutlinedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {row.status === "paid" && (
                            <Typography sx={{ fontSize: 11, color: "#94a3b8", display: "inline", ml: 1 }}>
                              {row.bambora_transaction_id ?? ""}
                            </Typography>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {total > ROWS_PER_PAGE && (
        <Box sx={{ p: 2 }}>
          <Pagination
            count={total}
            page={page}
            rowsPerPage={ROWS_PER_PAGE}
            onPageChange={(_: unknown, next: number) => setPage(next)}
          />
        </Box>
      )}
    </Box>
  );
};

export default PaymentLinksTab;
